import { supabase } from '../lib/supabase'
import type { Periodo } from '@praticheenergia/domain'

export type StatoDichiarazione =
  | 'letture_non_inserite'
  | 'bozza'
  | 'letture_da_controllare'
  | 'da_firmare'
  | 'da_inviare'
  | 'inviata'
  | 'errore_invio'

export interface DichiarazionePortale {
  readonly id: number
  readonly stato: StatoDichiarazione
  /** JSON grezzo di `dichiarazione_completa`: lo converte il motore con
   * `datiPeriodoDaJson`, il portale non lo reinterpreta. */
  readonly completa: unknown
  readonly ricevutaPercorso: string | null
  /** Ultimo esito ADM, presente solo in `errore_invio`. Li consuma il Task 8
   * con `spiegaErroreAdm`. */
  readonly erroreCodice: string | null
  readonly erroreDescrizione: string | null
  /** Tutti gli invii, dal piu' recente. Dopo una rettifica sono l'unica prova
   * di cio' che le Dogane hanno gia' ricevuto: `ricevutaPercorso` qui sopra
   * punta solo all'ultima ricevuta. */
  readonly invii: readonly InvioPortale[]
}

export interface InvioPortale {
  readonly id: number
  readonly esito: 'ricevuta' | 'merito' | 'trasporto' | null
  readonly protocollo: string | null
  readonly codiceErrore: string | null
  readonly descrizioneErrore: string | null
  readonly ricevutaPercorso: string | null
  /** Null per gli invii precedenti a 0041_rettifica.sql. */
  readonly xmlFirmatoPercorso: string | null
  readonly chiusoIl: string | null
  /** Ultimo stato di elaborazione ADM letto per IUT (0046); null finche' il worker non l'ha chiesto. */
  readonly statoAdm: string | null
  /** File di esito ADM firmato, solo per gli stati «con esito» (0047). */
  readonly esitoPercorso: string | null
}

interface RigaInvio {
  id: number
  esito: InvioPortale['esito']
  protocollo: string | null
  codice_errore: string | null
  descrizione_errore: string | null
  ricevuta_percorso: string | null
  xml_firmato_percorso: string | null
  chiuso_il: string | null
  stato_adm?: string | null
  esito_percorso?: string | null
}

export async function caricaDichiarazione(
  impiantoId: number,
  anno: number,
  periodo: Periodo,
): Promise<DichiarazionePortale | null> {
  // `invio` ordinato per id decrescente: l'ultimo tentativo e' quello che
  // spiega lo stato corrente, non il primo.
  const { data, error } = await supabase
    .from('dichiarazione')
    .select(
      'id, stato, ricevuta_percorso, invio(id, esito, protocollo, codice_errore, descrizione_errore, ricevuta_percorso, xml_firmato_percorso, chiuso_il, stato_adm, esito_percorso)',
    )
    .eq('impianto_id', impiantoId)
    .eq('anno_imposta', anno)
    .eq('periodo', periodo)
    .order('id', { referencedTable: 'invio', ascending: false })
    .maybeSingle()
  if (error) throw new Error(error.message)
  if (!data) return null

  const { data: completa, error: erroreCompleta } = await supabase.rpc('dichiarazione_completa', { p_id: data.id })
  if (erroreCompleta) throw new Error(erroreCompleta.message)

  const righe = (data.invio as RigaInvio[] | null) ?? []
  const ultimoInvio = righe[0]

  return {
    id: data.id as number,
    stato: data.stato as StatoDichiarazione,
    completa,
    ricevutaPercorso: (data.ricevuta_percorso as string | null) ?? null,
    erroreCodice: ultimoInvio?.codice_errore ?? null,
    erroreDescrizione: ultimoInvio?.descrizione_errore ?? null,
    invii: righe.map((r) => ({
      id: r.id,
      esito: r.esito,
      protocollo: r.protocollo,
      codiceErrore: r.codice_errore,
      descrizioneErrore: r.descrizione_errore,
      ricevutaPercorso: r.ricevuta_percorso,
      xmlFirmatoPercorso: r.xml_firmato_percorso,
      chiusoIl: r.chiuso_il,
      statoAdm: r.stato_adm ?? null,
      esitoPercorso: r.esito_percorso ?? null,
    })),
  }
}

export interface RiepilogoDichiarazione {
  readonly id: number
  readonly periodo: Periodo
  readonly stato: StatoDichiarazione
}

/** Idempotente: apre i periodi mancanti per la tipologia dell'impianto e
 * restituisce tutte le dichiarazioni dell'anno (0032_apertura_dichiarazione.sql). */
export async function apriDichiarazioni(impiantoId: number, anno: number): Promise<RiepilogoDichiarazione[]> {
  const { data, error } = await supabase.rpc('apri_dichiarazioni', { p_impianto_id: impiantoId, p_anno: anno })
  if (error) throw new Error(error.message)
  return ((data ?? []) as { id: number; periodo: Periodo; stato: StatoDichiarazione }[]).map((d) => ({
    id: d.id,
    periodo: d.periodo,
    stato: d.stato,
  }))
}

/** Accoda la generazione. `pgmq_send` resta riservata al service_role: qui si
 * passa dalla RPC che controlla il partner (0031_coda_generazione.sql). */
export async function confermaDichiarazione(dichiarazioneId: number): Promise<void> {
  const { error } = await supabase.rpc('accoda_generazione', { p_dichiarazione_id: dichiarazioneId })
  if (error) throw new Error(error.message)
}

/** Riapre in bozza una dichiarazione gia' trasmessa (0041_rettifica.sql). La
 * riga di `invio` con protocollo e ricevuta resta dov'e'. */
export async function rettificaDichiarazione(dichiarazioneId: number): Promise<void> {
  const { error } = await supabase.rpc('rettifica_dichiarazione', { p_dichiarazione_id: dichiarazioneId })
  if (error) throw new Error(error.message)
}

interface EsitoCaricamento {
  esito: 'accettato' | 'rifiutato'
  motivo?: string
}

/** La verifica e' sincrona apposta: l'errore piu' probabile e' «hai firmato
 * l'XML vecchio», e va detto mentre l'utente ha ancora il file sotto mano. */
export async function caricaFirmato(dichiarazioneId: number, file: File): Promise<void> {
  const contenuto = await file.text()
  const { data, error } = await supabase.functions.invoke('accetta-firmato', {
    body: { dichiarazioneId, nomeFile: file.name, contenuto },
  })
  if (error) throw new Error(error.message)
  const esito = data as EsitoCaricamento
  if (esito.esito === 'rifiutato') throw new Error(esito.motivo ?? 'File rifiutato')
}

/** Termine di presentazione per periodo, dalla regola fiscale dell'anno (0051). */
export async function terminiDichiarazioni(anno: number): Promise<Partial<Record<Periodo, string>>> {
  const { data, error } = await supabase
    .from('regola_fiscale')
    .select('periodo, termine:contenuto->>termine')
    .eq('anno_imposta', anno)
  if (error) throw new Error(error.message)
  return Object.fromEntries(
    ((data ?? []) as { periodo: Periodo; termine: string | null }[]).filter((r) => r.termine).map((r) => [r.periodo, r.termine]),
  )
}

export interface DichiarazioneInElenco {
  readonly id: number
  readonly impiantoId: number
  readonly impianto: string
  readonly cliente: string
  readonly periodo: Periodo
  readonly stato: StatoDichiarazione
}

type Annidato<T> = T | T[] | null
const uno = <T,>(v: Annidato<T>): T | null => (Array.isArray(v) ? (v[0] ?? null) : v)

/** Tutte le dichiarazioni dell'anno del partner (RLS), con impianto e cliente. */
export async function elencaDichiarazioni(anno: number): Promise<DichiarazioneInElenco[]> {
  const { data, error } = await supabase
    .from('dichiarazione')
    .select('id, impianto_id, periodo, stato, impianto(codice_impianto, codice_ditta, azienda(ragione_sociale))')
    .eq('anno_imposta', anno)
    .order('id')
  if (error) throw new Error(error.message)
  type Riga = {
    id: number
    impianto_id: number
    periodo: Periodo
    stato: StatoDichiarazione
    impianto: Annidato<{ codice_impianto: string | null; codice_ditta: string; azienda: Annidato<{ ragione_sociale: string }> }>
  }
  return ((data ?? []) as unknown as Riga[]).map((r) => {
    const i = uno(r.impianto)
    return {
      id: r.id,
      impiantoId: r.impianto_id,
      impianto: i?.codice_impianto ?? i?.codice_ditta ?? '',
      cliente: uno(i?.azienda ?? null)?.ragione_sociale ?? '',
      periodo: r.periodo,
      stato: r.stato,
    }
  })
}
